import React, { useEffect } from "react";
import type {
  Primitive,
  Step,
  StepConnection,
} from "../../types/TransactionTemplate.types";
import styles from "./TransactionTemplateEditor.module.css";
import { useDragPrimitive } from "../../hooks/transactions/useDragPrimitive";

interface TransactionSchemaProps {
  steps: Step[];
  stepConnections: StepConnection[];
  selectedPrimitive: { stepIndex: number; primIndex: number } | null;
  editingPrimitiveText: {
    stepIndex: number;
    primIndex: number;
    text: string;
  } | null;
  isSelectableTemplate: boolean;
  primaryColor: string;
  textColor: string;
  borderColor: string;
  svgRef: React.RefObject<SVGSVGElement | null>;
  onElementClick: (
    type: "event" | "primitive" | "step",
    stepIndex: number,
    primitiveIndex?: number,
  ) => void;
  onBackgroundClick: () => void;
  onDeleteStep: (stepIndex: number) => void;
  collapsedBranches: Set<string>;
  onToggleCollapse: (primId: string) => void;
  isEditMode: boolean;
  onPrimMouseDown: (
    e: React.MouseEvent,
    stepIndex: number,
    primId: string,
  ) => void;
  getIsDragging: () => boolean;
}

export default function TransactionSchema({
  steps,
  stepConnections,
  selectedPrimitive,
  editingPrimitiveText,
  isSelectableTemplate,
  primaryColor,
  textColor,
  borderColor,
  svgRef,
  onElementClick,
  onBackgroundClick,
  onDeleteStep,
  collapsedBranches,
  onToggleCollapse,
  isEditMode,
  onPrimMouseDown,
  getIsDragging,
}: TransactionSchemaProps) {
  const STEP_PADDING = 40;
  const PRIMITIVE_WIDTH = 120;
  const PROCESS_WIDTH = 130;
  const STEP_MIN_WIDTH = 900;
  const STEP_HEIGHT = 250;

  // Цвета для логики
  const COLOR_YES = "#199f4a";
  const COLOR_NO = "#ef4444";
  const STROKE_DASH = "5,3";
  const COLOR_RETURN = "#3b82f6";
  const COLOR_THROW = "#ef4444";
  const COLOR_BREAK = "#f59e0b";

  const getComputedColor = (variable: string, fallback: string) => {
    if (typeof window === "undefined") return fallback;
    const root = document.documentElement;
    const value = getComputedStyle(root).getPropertyValue(variable).trim();
    return value || fallback;
  };

  const getDescendantIds = (sourceId: string, step: Step): Set<string> => {
    const result = new Set<string>();
    const queue = [sourceId];
    while (queue.length > 0) {
      const current = queue.shift()!;
      step.connections
        .filter((c) => c.from === current)
        .forEach((c) => {
          if (!result.has(c.to)) {
            result.add(c.to);
            queue.push(c.to);
          }
        });
    }
    return result;
  };

  const getLightFill = (color: string) => {
    return color + "15";
  };

  const calculateStepHeight = (step: Step) => {
    if (!step.primitives.length && !step.event) return STEP_HEIGHT;

    let maxY = 0;

    if (step.event) {
      maxY = Math.max(maxY, step.event.y + 80);
    }

    step.primitives.forEach((prim) => {
      const primHeight = 80;
      maxY = Math.max(maxY, prim.y + primHeight);
    });

    const totalHeight = maxY + STEP_PADDING;

    return Math.max(totalHeight, STEP_HEIGHT);
  };

  const calculateStepWidth = (step: Step) => {
    if (!step.primitives.length) return STEP_MIN_WIDTH;

    const rightmostPrimitive = step.primitives.reduce((max, p) => {
      const pWidth = p.type === "process" ? PROCESS_WIDTH : PRIMITIVE_WIDTH;
      const pRight = p.x + pWidth;
      return pRight > max ? pRight : max;
    }, 0);

    const total = rightmostPrimitive + STEP_PADDING;

    return Math.max(total, STEP_MIN_WIDTH);
  };

  const calculateSchemaDimensions = () => {
    if (!steps.length) {
      return { width: 2000, height: 800 };
    }

    let maxWidth = 0;
    let maxHeight = 0;

    steps.forEach((step) => {
      step.primitives.forEach((prim) => {
        const primWidth =
          prim.type === "process" ? PROCESS_WIDTH : PRIMITIVE_WIDTH;
        const primRight = prim.x + primWidth;
        maxWidth = Math.max(maxWidth, primRight);
      });

      step.primitives.forEach((prim) => {
        const primBottom = step.y + prim.y + 80;
        maxHeight = Math.max(maxHeight, primBottom);
      });

      if (step.event) {
        const eventBottom = step.y + step.event.y + 80;
        maxHeight = Math.max(maxHeight, eventBottom);
      }
    });

    return {
      width: Math.max(maxWidth + 200, 2000),
      height: Math.max(maxHeight + 200, 800),
    };
  };

  const renderStepConnections = (stepConnections: StepConnection[]) => {
    return stepConnections.map((conn, idx) => {
      const fromStep = steps.find((s) => s.id === conn.from);
      const toStep = steps.find((s) => s.id === conn.to);

      if (!fromStep || !toStep) return null;

      const fromStepWidth = calculateStepWidth(fromStep);
      const fromStepHeight = calculateStepHeight(fromStep);
      const toStepWidth = calculateStepWidth(toStep);

      const fromX = 20 + fromStepWidth / 2;
      const fromY = fromStep.y + fromStepHeight;
      const toX = 20 + toStepWidth / 2;
      const toY = toStep.y;

      return (
        <line
          key={`step-conn-${idx}`}
          x1={fromX}
          y1={fromY}
          x2={toX}
          y2={toY}
          stroke="#000000"
          strokeWidth="3"
          strokeDasharray="5,5"
          markerEnd="url(#arrowhead-step)"
          style={{ pointerEvents: "none" }}
        />
      );
    });
  };

  const renderEvent = (event: Step["event"], step: Step, stepIndex: number) => {
    if (!event) return null;

    const baseX = event.x;
    const baseY = step.y + event.y;

    const isSelected =
      selectedPrimitive?.stepIndex === stepIndex &&
      selectedPrimitive?.primIndex === -1;

    const themeColor = getComputedColor("--prim", "#3b82f6");
    const strokeColor = isSelected ? themeColor : "#000000";
    const strokeWidth = isSelected ? 4 : 2;

    const isStart =
      event.name === "transaction_start" || event.name === "start";

    if (isStart) {
      const ovalWidth = 100;
      const ovalHeight = 60;
      const cx = baseX + ovalWidth / 2;
      const cy = baseY + ovalHeight / 2;
      const rx = ovalWidth / 2;
      const ry = ovalHeight / 2;

      const interactionArea = (
        <ellipse
          cx={cx}
          cy={cy}
          rx={rx}
          ry={ry}
          fill="white"
          fillOpacity="0"
          stroke="none"
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            onElementClick("event", stepIndex);
          }}
        />
      );

      const eventText = (() => {
        const words = event.name.split("_");
        const lines =
          words.length > 2
            ? [words.slice(0, 2).join("_"), words.slice(2).join("_")]
            : words;
        const displayLines = lines.slice(0, 2);
        return displayLines.map((line, idx) => (
          <text
            key={idx}
            x={cx}
            y={cy - 6 + idx * 12}
            fill={textColor}
            fontSize="8"
            fontWeight="500"
            textAnchor="middle"
            style={{ pointerEvents: "none" }}
          >
            {line.length > 12 ? line.substring(0, 11) + "..." : line}
          </text>
        ));
      })();

      return (
        <g>
          {interactionArea}
          <ellipse
            cx={cx}
            cy={cy}
            rx={rx}
            ry={ry}
            fill="white"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            style={{ pointerEvents: "none" }}
          />
          {eventText}
        </g>
      );
    } else {
      const interactionArea = (
        <polygon
          points={`${baseX},${baseY} ${baseX + 70},${baseY + 40} ${baseX},${baseY + 80}`}
          fill="white"
          fillOpacity="0"
          stroke="none"
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            onElementClick("event", stepIndex);
          }}
        />
      );

      const eventText = (() => {
        const words = event.name.split("_");
        const lines =
          words.length > 2
            ? [words.slice(0, 2).join("_"), words.slice(2).join("_")]
            : words;
        const displayLines = lines.slice(0, 2);
        return displayLines.map((line, idx) => (
          <text
            key={idx}
            x={baseX + 20}
            y={baseY + 35 + idx * 12}
            fill={textColor}
            fontSize="8"
            fontWeight="500"
            style={{ pointerEvents: "none" }}
          >
            {line.length > 10 ? line.substring(0, 9) + "..." : line}
          </text>
        ));
      })();

      return (
        <g>
          {interactionArea}
          <polygon
            points={`${baseX},${baseY} ${baseX + 70},${baseY + 40} ${baseX},${baseY + 80}`}
            fill="white"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            style={{ pointerEvents: "none" }}
          />
          {eventText}
        </g>
      );
    }
  };

  const renderPrimitive = (
    prim: Primitive,
    step: Step,
    stepIndex: number,
    primIndex: number,
  ) => {
    const baseX = prim.x;
    const baseY = step.y + prim.y;
    const width = prim.type === "process" ? 130 : 120;
    const height = 80;

    const isSelected =
      selectedPrimitive?.stepIndex === stepIndex &&
      selectedPrimitive?.primIndex === primIndex;

    const color = (prim as any).color || primaryColor;
    const strokeWidth = isSelected ? 4 : 2;
    const themeColor = getComputedColor("--prim", "#3b82f6");
    const strokeColor = isSelected ? themeColor : color;
    const fillColor = "white";

    const interactionArea = (
      <rect
        x={baseX}
        y={baseY}
        width={width}
        height={height}
        fill="white"
        fillOpacity="0"
        stroke="none"
        style={{ cursor: "pointer" }}
        onMouseDown={(e) => onPrimMouseDown(e, stepIndex, prim.id)}
        onClick={(e) => {
          if (getIsDragging()) return;
          e.stopPropagation();
          onElementClick("primitive", stepIndex, primIndex);
        }}
      />
    );

    const primitiveText = (() => {
      const maxWidth = width - 10;
      const words = prim.name.split(/[\s_]/);
      const lines: string[] = [];
      let currentLine = "";

      words.forEach((word) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        if (testLine.length * 6 < maxWidth) {
          currentLine = testLine;
        } else {
          if (currentLine) lines.push(currentLine);
          currentLine = word;
        }
      });
      if (currentLine) lines.push(currentLine);

      const displayLines = lines.slice(0, 3);
      if (lines.length > 3) {
        displayLines[2] = displayLines[2].substring(0, 10) + "...";
      }

      return displayLines.map((line, idx) => (
        <text
          key={idx}
          x={baseX + width / 2}
          y={baseY + 28 + idx * 12}
          fill={textColor}
          fontSize="9"
          textAnchor="middle"
          fontWeight="500"
          style={{ pointerEvents: "none" }}
        >
          {line}
        </text>
      ));
    })();

    switch (prim.type) {
      case "loop": {
        const hexWidth = 120;
        const hexHeight = 80;
        const offset = 20;
        return (
          <g>
            {interactionArea}
            <polygon
              points={`
                ${baseX + offset},${baseY}
                ${baseX + hexWidth - offset},${baseY}
                ${baseX + hexWidth},${baseY + hexHeight / 2}
                ${baseX + hexWidth - offset},${baseY + hexHeight}
                ${baseX + offset},${baseY + hexHeight}
                ${baseX},${baseY + hexHeight / 2}
              `}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              style={{ pointerEvents: "none" }}
            />
            {primitiveText}
          </g>
        );
      }

      case "condition":
        return (
          <g>
            {interactionArea}
            <polygon
              points={`${baseX + width / 2},${baseY} ${baseX + width},${baseY + height / 2} ${baseX + width / 2},${baseY + height} ${baseX},${baseY + height / 2}`}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              style={{ pointerEvents: "none" }}
            />
            {primitiveText}
          </g>
        );

      case "process":
        return (
          <g>
            {interactionArea}
            <rect
              x={baseX}
              y={baseY}
              width={width}
              height={height}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              rx="3"
              style={{ pointerEvents: "none" }}
            />
            <line
              x1={baseX + 10}
              y1={baseY}
              x2={baseX + 10}
              y2={baseY + height}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              style={{ pointerEvents: "none" }}
            />
            <line
              x1={baseX + width - 10}
              y1={baseY}
              x2={baseX + width - 10}
              y2={baseY + height}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              style={{ pointerEvents: "none" }}
            />
            {primitiveText}
          </g>
        );

      case "break":
        const breakColor = COLOR_BREAK;
        return (
          <g>
            {interactionArea}
            <rect
              x={baseX}
              y={baseY}
              width={width}
              height={height}
              fill={getLightFill(breakColor)}
              stroke={breakColor}
              strokeWidth={strokeWidth}
              strokeDasharray={STROKE_DASH}
              rx="30"
              style={{ pointerEvents: "none" }}
            />
            {primitiveText}
          </g>
        );

      case "function":
        return (
          <g>
            {interactionArea}
            <rect
              x={baseX}
              y={baseY}
              width={width}
              height={height}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              rx="3"
              style={{ pointerEvents: "none" }}
            />
            <line
              x1={baseX}
              y1={baseY + 12}
              x2={baseX + width}
              y2={baseY + 12}
              stroke={strokeColor}
              strokeWidth={1.5}
              style={{ pointerEvents: "none" }}
            />
            <line
              x1={baseX}
              y1={baseY + height - 12}
              x2={baseX + width}
              y2={baseY + height - 12}
              stroke={strokeColor}
              strokeWidth={1.5}
              style={{ pointerEvents: "none" }}
            />
            {primitiveText}
          </g>
        );

      case "return":
        const returnColor = COLOR_RETURN;
        return (
          <g>
            {interactionArea}
            <rect
              x={baseX}
              y={baseY}
              width={width}
              height={height}
              fill={getLightFill(returnColor)}
              stroke={returnColor}
              strokeWidth={strokeWidth}
              strokeDasharray={STROKE_DASH}
              rx="10"
              style={{ pointerEvents: "none" }}
            />
            <path
              d={`M ${baseX + 28} ${baseY + height / 2} L ${baseX + 15} ${baseY + height / 2} M ${baseX + 15} ${baseY + height / 2} L ${baseX + 21} ${baseY + height / 2 - 6} M ${baseX + 15} ${baseY + height / 2} L ${baseX + 21} ${baseY + height / 2 + 6}`}
              stroke={returnColor}
              strokeWidth={2}
              fill="none"
              style={{ pointerEvents: "none" }}
            />
            {primitiveText}
          </g>
        );

      case "throw": {
        const ox = 25;
        const oy = 18;
        const throwColor = COLOR_THROW;
        return (
          <g>
            {interactionArea}
            <polygon
              points={`
                ${baseX + ox},${baseY}
                ${baseX + width - ox},${baseY}
                ${baseX + width},${baseY + oy}
                ${baseX + width},${baseY + height - oy}
                ${baseX + width - ox},${baseY + height}
                ${baseX + ox},${baseY + height}
                ${baseX},${baseY + height - oy}
                ${baseX},${baseY + oy}
              `}
              fill={getLightFill(throwColor)}
              stroke={throwColor}
              strokeWidth={strokeWidth}
              strokeDasharray={STROKE_DASH}
              style={{ pointerEvents: "none" }}
            />
            {primitiveText}
          </g>
        );
      }

      case "subtransaction":
        return (
          <g>
            {interactionArea}
            <rect
              x={baseX}
              y={baseY}
              width={width}
              height={height}
              fill="none"
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              rx="3"
              style={{ pointerEvents: "none" }}
            />
            <rect
              x={baseX + 5}
              y={baseY + 5}
              width={width - 10}
              height={height - 10}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={1.5}
              rx="2"
              style={{ pointerEvents: "none" }}
            />
            {primitiveText}
          </g>
        );

      case "primitive":
      default:
        return (
          <g>
            {interactionArea}
            <rect
              x={baseX}
              y={baseY}
              width={width}
              height={height}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              rx="3"
              style={{ pointerEvents: "none" }}
            />
            {primitiveText}
          </g>
        );
    }
  };

  return (
    <svg
      ref={svgRef}
      tabIndex={0}
      style={{ outline: "none" }}
      className={styles.svg}
      width={calculateSchemaDimensions().width}
      height={calculateSchemaDimensions().height}
      onClick={onBackgroundClick}
    >
      <defs>
        <marker
          id="arrowhead"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
        >
          <polygon points="0 0, 10 3, 0 6" fill={primaryColor} />
        </marker>
        {/* Зеленая стрелка */}
        <marker
          id="arrowhead-yes"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
        >
          <polygon points="0 0, 10 3, 0 6" fill={COLOR_YES} />
        </marker>
        {/* Красная стрелка */}
        <marker
          id="arrowhead-no"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
        >
          <polygon points="0 0, 10 3, 0 6" fill={COLOR_NO} />
        </marker>
        <marker
          id="arrowhead-step"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
        >
          <polygon points="0 0, 10 3, 0 6" fill="#000000" />
        </marker>
      </defs>

      {renderStepConnections(stepConnections)}

      {steps.map((step, stepIndex) => {
        const stepWidth = calculateStepWidth(step);
        const stepHeight = calculateStepHeight(step);

        const hiddenPrimIds = new Set<string>();
        collapsedBranches.forEach((collapsedId) => {
          const prim = step.primitives.find((p) => p.id === collapsedId);
          if (prim) {
            getDescendantIds(collapsedId, step).forEach((id) =>
              hiddenPrimIds.add(id),
            );
          }
        });

        return (
          <g key={step.id}>
            {isSelectableTemplate && (
              <g
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteStep(stepIndex);
                }}
                style={{ cursor: "pointer" }}
              >
                <rect
                  x={20 + stepWidth - 28}
                  y={step.y + 8}
                  width="20"
                  height="20"
                  rx="4"
                  fill="#fff"
                  stroke="#ef4444"
                  strokeWidth="1"
                />
                <line
                  x1={20 + stepWidth - 24}
                  y1={step.y + 12}
                  x2={20 + stepWidth - 12}
                  y2={step.y + 24}
                  stroke="#ef4444"
                  strokeWidth="2"
                />
                <line
                  x1={20 + stepWidth - 12}
                  y1={step.y + 12}
                  x2={20 + stepWidth - 24}
                  y2={step.y + 24}
                  stroke="#ef4444"
                  strokeWidth="2"
                />
              </g>
            )}

            {step.connections
              .filter(
                (conn) =>
                  !hiddenPrimIds.has(conn.to) && !hiddenPrimIds.has(conn.from),
              )
              .map((conn, idx) => {
                const isFromEvent = conn.from === step.event?.id;
                const fromElement = isFromEvent
                  ? step.event
                  : step.primitives.find((p) => p.id === conn.from);
                const toElement = step.primitives.find((p) => p.id === conn.to);

                if (!fromElement || !toElement) return null;

                let fromX, fromY, toX, toY, pathD;
                let midX, midY;

                let outRightX,
                  outRightY,
                  outBottomX,
                  outBottomY,
                  outTopX,
                  outTopY;
                let sourceCenterX;

                if (isFromEvent && step.event) {
                  const isStart =
                    step.event.name === "transaction_start" ||
                    step.event.name === "start";
                  if (isStart) {
                    sourceCenterX = fromElement.x + 50;
                    outRightX = fromElement.x + 100;
                    outRightY = step.y + fromElement.y + 30;
                    outBottomX = sourceCenterX;
                    outBottomY = step.y + fromElement.y + 60;
                    outTopX = sourceCenterX;
                    outTopY = step.y + fromElement.y;
                  } else {
                    sourceCenterX = fromElement.x + 35;
                    outRightX = fromElement.x + 70;
                    outRightY = step.y + fromElement.y + 40;
                    outBottomX = sourceCenterX;
                    outBottomY = step.y + fromElement.y + 80;
                    outTopX = sourceCenterX;
                    outTopY = step.y + fromElement.y;
                  }
                } else {
                  const prim = fromElement as Primitive;
                  const primWidth = prim.type === "process" ? 130 : 120;
                  sourceCenterX = prim.x + primWidth / 2;
                  outRightX = prim.x + primWidth;
                  outRightY = step.y + prim.y + 40;
                  outBottomX = sourceCenterX;
                  outBottomY = step.y + prim.y + 80;
                  outTopX = sourceCenterX;
                  outTopY = step.y + prim.y;
                }

                const toWidth = toElement.type === "process" ? 130 : 120;
                const targetCenterX = toElement.x + toWidth / 2;

                const inLeftX = toElement.x;
                const inLeftY = step.y + toElement.y + 40;

                const inTopX = targetCenterX;
                const inTopY = step.y + toElement.y;

                const inBottomX = targetCenterX;
                const inBottomY = step.y + toElement.y + 80;

                const radius = 20;

                const isVerticallyAligned =
                  Math.abs(sourceCenterX - targetCenterX) < 15;
                const sameRow = Math.abs(outRightY - inLeftY) < 60;

                if (isVerticallyAligned && !sameRow) {
                  const isBelow = inTopY > outBottomY;

                  if (isBelow) {
                    fromX = outBottomX;
                    fromY = outBottomY;
                    toX = inTopX;
                    toY = inTopY;
                  } else {
                    fromX = outRightX;
                    fromY = outRightY;
                    toX = inLeftX;
                    toY = inLeftY;

                    const dropX = fromX + 20;

                    pathD = `M ${fromX} ${fromY} L ${dropX} ${fromY} L ${dropX} ${toY} L ${toX} ${toY}`;
                    midX = dropX;
                    midY = (fromY + toY) / 2;
                  }

                  pathD = `M ${fromX} ${fromY} L ${toX} ${toY}`;
                  midX = fromX;
                  midY = (fromY + toY) / 2;
                } else if (sameRow) {
                  fromX = outRightX;
                  fromY = outRightY;
                  toX = inLeftX;
                  toY = inLeftY;

                  if (toX >= fromX) {
                    pathD = `M ${fromX} ${fromY} L ${toX} ${toY}`;
                    midX = (fromX + toX) / 2;
                    midY = (fromY + toY) / 2;
                  } else {
                    const dropY = Math.max(outBottomY + 20, toY + 50);
                    pathD = `M ${fromX} ${fromY} L ${fromX + 20} ${fromY} L ${fromX + 20} ${dropY} L ${toX - 20} ${dropY} L ${toX - 20} ${toY} L ${toX} ${toY}`;
                    midX = fromX + 20;
                    midY = dropY;
                  }
                } else {
                  toX = inLeftX;
                  toY = inLeftY;
                  const isBelow = toY > outBottomY;

                  if (isBelow) {
                    fromX = outBottomX;
                    fromY = outBottomY;
                    midX = fromX;
                    midY = (fromY + toY - radius) / 2;

                    const actualRadius = Math.min(
                      radius,
                      Math.abs(toY - fromY) / 2,
                    );
                    const dirX = toX >= fromX ? actualRadius : -actualRadius;

                    pathD = `M ${fromX} ${fromY} L ${fromX} ${toY - actualRadius} Q ${fromX} ${toY} ${fromX + dirX} ${toY} L ${toX} ${toY}`;
                  } else {
                    fromX = outTopX;
                    fromY = outTopY;
                    midX = fromX;
                    midY = (fromY + toY + radius) / 2;

                    const actualRadius = Math.min(
                      radius,
                      Math.abs(fromY - toY) / 2,
                    );
                    const dirX = toX >= fromX ? actualRadius : -actualRadius;

                    pathD = `M ${fromX} ${fromY} L ${fromX} ${toY + actualRadius} Q ${fromX} ${toY} ${fromX + dirX} ${toY} L ${toX} ${toY}`;
                  }
                }

                let strokeColor = primaryColor;
                let markerEnd = "url(#arrowhead)";
                let labelText = "";
                let labelBgWidth = 0;

                const fromType = (fromElement as any).type;
                const isSourceBranching =
                  !isFromEvent &&
                  ["condition", "if", "loop", "for"].includes(fromType);

                if (isSourceBranching) {
                  if (sameRow) {
                    strokeColor = primaryColor;
                    markerEnd = "url(#arrowhead)";
                    labelText = "";
                  } else {
                    if (conn.branch === "yes") {
                      strokeColor = COLOR_YES;
                      markerEnd = "url(#arrowhead-yes)";
                      labelText = "Yes";
                      labelBgWidth = 28;
                    } else if (conn.branch === "no") {
                      strokeColor = COLOR_NO;
                      markerEnd = "url(#arrowhead-no)";
                      labelText = "No";
                      labelBgWidth = 24;
                    }
                  }
                }

                const labelX = sameRow ? midX : midX - 15;
                const labelY = midY;

                return (
                  <g key={`${conn.from}-${conn.to}-${idx}`}>
                    <path
                      d={pathD}
                      fill="none"
                      stroke="transparent"
                      strokeWidth="15"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log("Conn:", conn);
                      }}
                    />
                    <path
                      d={pathD}
                      fill="none"
                      stroke={strokeColor}
                      strokeWidth="2"
                      markerEnd={markerEnd}
                      style={{
                        pointerEvents: "none",
                        transition: "stroke 0.3s",
                      }}
                    />
                    {labelText && (
                      <g style={{ pointerEvents: "none" }}>
                        <rect
                          x={labelX - labelBgWidth / 2}
                          y={labelY - 8}
                          width={labelBgWidth}
                          height="16"
                          fill="white"
                          fillOpacity="0.9"
                          rx="4"
                        />
                        <text
                          x={labelX}
                          y={labelY}
                          fill={strokeColor}
                          fontSize="10"
                          textAnchor="middle"
                          dy="4"
                          fontWeight="bold"
                        >
                          {labelText}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}

            <rect
              x="20"
              y={step.y}
              width={stepWidth}
              height={stepHeight}
              fill="transparent"
              // stroke={borderColor}
              // strokeWidth="2"
              rx="5"
              style={{ pointerEvents: "none" }}
            />

            {renderEvent(step.event, step, stepIndex)}

            {step.primitives.map((prim, primIndex) => {
              if (hiddenPrimIds.has(prim.id)) return null;

              const isCollapsed = collapsedBranches.has(prim.id);
              const hasOutgoing = step.connections.some(
                (c) => c.from === prim.id,
              );
              const baseX = prim.x;
              const baseY = step.y + prim.y;
              const width = prim.type === "process" ? 130 : 120;

              return (
                <g key={`prim-${stepIndex}-${primIndex}`}>
                  {renderPrimitive(prim, step, stepIndex, primIndex)}

                  {/* кнопка чтобы свернуть */}
                  {hasOutgoing && (
                    <g
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleCollapse(prim.id);
                      }}
                    >
                      <rect
                        x={baseX + width - 10}
                        y={baseY - 22}
                        width="20"
                        height="16"
                        rx="4"
                        fill={isCollapsed ? "#e8f4fd" : "#f0f0f0"}
                        stroke={isCollapsed ? "#3b82f6" : "#ccc"}
                        strokeWidth="1"
                      />
                      <text
                        x={baseX + width}
                        y={baseY - 11}
                        fontSize="13"
                        textAnchor="middle"
                        fill={isCollapsed ? "#3b82f6" : "#888"}
                        fontWeight="bold"
                        style={{ pointerEvents: "none", userSelect: "none" }}
                      >
                        {isCollapsed ? "+" : "−"}
                      </text>
                    </g>
                  )}

                  {/* если свернуто */}
                  {isCollapsed &&
                    (() => {
                      const count = getDescendantIds(prim.id, step).size;
                      return (
                        <g
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleCollapse(prim.id);
                          }}
                        >
                          <rect
                            x={baseX + width + 20}
                            y={baseY + 20}
                            width="70"
                            height="30"
                            rx="6"
                            fill="#f5f5f5"
                            stroke="#bbb"
                            strokeWidth="1"
                            strokeDasharray="4,2"
                          />
                          <text
                            x={baseX + width + 55}
                            y={baseY + 40}
                            fontSize="9"
                            textAnchor="middle"
                            fill="#888"
                            style={{ pointerEvents: "none" }}
                          >
                            ···{count}
                          </text>
                        </g>
                      );
                    })()}
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}
